import { Component, input, output } from '@angular/core';
import { CancellationFlowDetailsResponse } from 'src/app/_types/cancellation.types';
import { getCancellationCancelReasonLabel } from 'src/app/enums/cancellation/cancellation-cancel-reason.enum';
import {
  CancellationFlowStatus,
  isReplacementReady,
} from 'src/app/enums/cancellation/cancellation-flow-status.enum';
import { NoticeCardComponent } from 'src/app/shared/ui-kit/notice-card/notice-card.component';

import { CancellationCardVariant } from './cancellation-card-variant';

/**
 * Where the cancellation letter stands, read off the status alone, and the one
 * thing to do about it: produce the letter, file the signed one, get it to
 * the insurer, then close the file. The button only asks the host to act — the host owns the forms,
 * as it owns the modal that hosts this card. Without `canEdit` there is no button.
 */
@Component({
  selector: 'app-cancellation-letter-card',
  standalone: true,
  imports: [NoticeCardComponent],
  template: `
    <app-notice-card
      [tone]="variant.tone"
      [title]="variant.title"
      [pill]="variant.pill"
      [pillClass]="variant.pillClass"
      [description]="variant.description"
      [actionLabel]="variant.actionLabel"
      [actionStyle]="variant.actionStyle ?? 'outline'"
      [actionDisabled]="variant.actionDisabled ?? false"
      [actionTooltip]="variant.actionTooltip"
      (action)="action.emit()"
    />
  `,
})
export class CancellationLetterCardComponent {
  readonly cancellation = input.required<CancellationFlowDetailsResponse>();

  readonly canEdit = input(false);

  /** The button: open the form the status calls for. */
  readonly action = output<void>();

  protected get variant(): CancellationCardVariant {
    const variant = this.statusVariant();
    if (!variant.actionLabel) return variant;
    if (!this.canEdit()) return { ...variant, actionLabel: null };
    if (!this.incomplete) return variant;
    // Nothing moves forward while the request does not identify the policy it
    // ends; the server refuses it too.
    const reason = $localize`Complétez le type d'assurance, l'assureur et le n° de police pour continuer.`;
    return {
      ...variant,
      description: variant.description ?? reason,
      actionDisabled: true,
      actionTooltip: reason,
    };
  }

  /** A request that does not yet say which policy it ends. */
  private get incomplete(): boolean {
    const cancellation = this.cancellation();
    return (
      !cancellation.insuranceType ||
      !cancellation.insurer.categoryId ||
      !cancellation.policyNumber
    );
  }

  private statusVariant(): CancellationCardVariant {
    switch (this.cancellation().status) {
      case CancellationFlowStatus.PENDING:
        return {
          tone: 'warning',
          title: $localize`Document non généré pour le client`,
          actionLabel: $localize`Générer ou insérer la lettre`,
        };
      case CancellationFlowStatus.LETTER_GENERATED:
        return {
          tone: 'warning',
          title: $localize`Document non signé par le client`,
          actionLabel: $localize`Importer la lettre signée`,
        };
      case CancellationFlowStatus.SIGNED: {
        const replacement = this.cancellation().replacementInsuranceFlow;
        // What the letter tells the insurer, and who has to be ready for it.
        const reason = !this.cancellation().cancellationDate
          ? $localize`Renseignez la date de résiliation avant l'envoi à l'assureur.`
          : replacement && !isReplacementReady(replacement.status)
            ? $localize`Envoi possible dès que la police de remplacement sera active ou en attente d'activation.`
            : null;
        return {
          tone: 'success',
          title: $localize`Lettre de résiliation signée par le client`,
          description: reason,
          actionLabel: $localize`Marquer comme envoyée`,
          actionStyle: 'primary',
          actionDisabled: !!reason,
          actionTooltip: reason,
        };
      }
      case CancellationFlowStatus.SENT:
        return {
          tone: 'success',
          title: $localize`Lettre envoyée à l'assureur`,
          actionLabel: $localize`Finaliser la résiliation`,
          actionStyle: 'primary',
        };
      case CancellationFlowStatus.CLOSED:
        return { tone: 'success', title: $localize`Résiliation effective` };
      case CancellationFlowStatus.CANCELLED: {
        const reason = this.cancellation().cancelReason;
        return {
          tone: 'error',
          title: $localize`Demande annulée`,
          description: reason ? getCancellationCancelReasonLabel(reason) : null,
        };
      }
      default:
        return { tone: 'plain', title: this.cancellation().status };
    }
  }
}
